/**
 * Users Hooks
 * TanStack Query hooks for user management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getUsers,
    getUser,
    getCurrentUser,
    createUser,
    updateUser,
    updateCurrentUser,
    deleteUser,
    deleteUsers,
    inviteUsers,
    type User,
    type CreateUserPayload,
    type UpdateUserPayload,
    type InviteUserPayload,
    type UsersQuery,
} from '@/api/users';
import { toast } from 'sonner';

// Query keys
export const usersKeys = {
    all: ['users'] as const,
    list: (query?: UsersQuery) => [...usersKeys.all, 'list', query] as const,
    detail: (id: string) => [...usersKeys.all, 'detail', id] as const,
    me: () => [...usersKeys.all, 'me'] as const,
};

// ============== Queries ==============

/**
 * Get all users
 */
export function useUsers(query?: UsersQuery) {
    return useQuery({
        queryKey: usersKeys.list(query),
        queryFn: () => getUsers(query),
    });
}

/**
 * Get users by role
 */
export function useUsersByRole(roleId: string | null, query?: Omit<UsersQuery, 'filter'>) {
    const fullQuery: UsersQuery = {
        ...query,
        filter: roleId ? { role: { _eq: roleId } } : undefined,
    };

    return useQuery({
        queryKey: usersKeys.list(fullQuery),
        queryFn: () => getUsers(fullQuery),
        enabled: true, // Always enabled, shows all users when roleId is null
    });
}

/**
 * Get a single user
 */
export function useUser(id: string, options?: { enabled?: boolean; fields?: string[] }) {
    return useQuery({
        queryKey: usersKeys.detail(id),
        queryFn: () => getUser(id, options?.fields || ['*']),
        enabled: options?.enabled ?? !!id,
    });
}

/**
 * Get current user
 */
export function useCurrentUser(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: usersKeys.me(),
        queryFn: () => getCurrentUser(['*']),
        enabled: options?.enabled ?? true,
    });
}

// ============== Mutations ==============

/**
 * Create a new user
 */
export function useCreateUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: CreateUserPayload) => createUser(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            toast.success('User created successfully');
        },
        onError: (error: Error) => {
            toast.error(`Failed to create user: ${error.message}`);
        },
    });
}

/**
 * Update a user
 */
export function useUpdateUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
            updateUser(id, payload),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            queryClient.invalidateQueries({ queryKey: usersKeys.detail(variables.id) });
            toast.success('User updated successfully');
        },
        onError: (error: Error) => {
            toast.error(`Failed to update user: ${error.message}`);
        },
    });
}

/**
 * Update current user
 */
export function useUpdateCurrentUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: UpdateUserPayload) => updateCurrentUser(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: usersKeys.me() });
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            toast.success('Profile updated successfully');
        },
        onError: (error: Error) => {
            toast.error(`Failed to update profile: ${error.message}`);
        },
    });
}

/**
 * Delete a user
 */
export function useDeleteUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteUser(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            toast.success('User deleted successfully');
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete user: ${error.message}`);
        },
    });
}

/**
 * Delete multiple users
 */
export function useDeleteUsers() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: string[]) => deleteUsers(ids),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            toast.success(`${variables.length} user(s) deleted successfully`);
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete users: ${error.message}`);
        },
    });
}

/**
 * Invite users
 */
export function useInviteUsers() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: InviteUserPayload) => inviteUsers(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
            toast.success('Invitation(s) sent successfully');
        },
        onError: (error: Error) => {
            toast.error(`Failed to send invitation: ${error.message}`);
        },
    });
}

// Re-export types and utilities
export type { User, CreateUserPayload, UpdateUserPayload, InviteUserPayload, UsersQuery };
export { getUserDisplayName, getUserInitials, getStatusColor } from '@/api/users';
